import { logisticsApi } from "./apiClient";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  activityStatusSchema,
  isGroup,
  timeOfDaySchema,
} from "@medoma/models/activities";
import {
  unenrichedClinicVisitActivityOccurrenceSchema,
  unenrichedGroupOfClinicVisitActivityOccurrenceSchema,
  unenrichedGroupOfHomeVisitActivityOccurrenceSchema,
  unenrichedHomeVisitActivityOccurrenceSchema,
  unenrichedPatientMeasurementTaskActivityOccurrenceSchema,
  unenrichedPatientTaskActivityOccurrenceSchema,
  unenrichedVideoActivityOccurrenceSchema,
} from "@medoma/models/activities.unenriched";
import { employee, scheduledShiftSchema } from "@medoma/models/shifts";
import { addDays, isSameDay, startOfToday } from "date-fns";
import { usePatient } from "./patient";
import { generateQueryString } from "@/api/Helpers";
import { machineFormat } from "@medoma/models/date-and-time";

// Care is read by the patient, so the schedule accepts only the fields it shows them.
// Everything else the backend sends — the internal note and description, the staffing
// requirement, whether the visit is double-staffed, and the assignees' shift records —
// is dropped at this boundary. Picked from the shared per-category schemas so the
// shapes stay in step, and per category because what the patient is shown differs
// between them: only a measurement task lists measurements, and only a staffed
// category names who is coming.
const shiftSchema = scheduledShiftSchema
  .pick({ id: true, competence: true })
  .extend({
    employee: employee
      .pick({
        firstName: true,
        lastName: true,
        competence: true,
        imageURL: true,
      })
      .nullish(),
  });
export type IShift = z.infer<typeof shiftSchema>;

/**
 * A shift with nobody on it has nobody to name, so the patient is never told about
 * it. Consumers that show assignees take an assigned shift and narrow to it here.
 */
export type IAssignedShift = StrictOmit<IShift, "employee"> & {
  employee: NonNullable<IShift["employee"]>;
};
export const isAssigned = (shift: IShift): shift is IAssignedShift =>
  Boolean(shift.employee);

const assignees = { assignees: z.array(shiftSchema) };

const shownOccurrenceFields = {
  activityId: true,
  id: true,
  category: true,
  title: true,
  start: true,
  end: true,
  timeOfDay: true,
  status: true,
  finishedAt: true,
} as const;

const homeVisitSchema = unenrichedHomeVisitActivityOccurrenceSchema
  .pick(shownOccurrenceFields)
  .extend(assignees);

export const videoCallSchema = unenrichedVideoActivityOccurrenceSchema
  .pick(shownOccurrenceFields)
  .extend(assignees);

export const clinicVisitSchema = unenrichedClinicVisitActivityOccurrenceSchema
  .pick(shownOccurrenceFields)
  .extend(assignees);

const patientTaskSchema = unenrichedPatientTaskActivityOccurrenceSchema.pick(
  shownOccurrenceFields,
);

const measurementTaskSchema =
  unenrichedPatientMeasurementTaskActivityOccurrenceSchema.pick({
    ...shownOccurrenceFields,
    measurements: true,
  });

const occurrenceSchema = z.union([
  homeVisitSchema,
  videoCallSchema,
  clinicVisitSchema,
  patientTaskSchema,
  measurementTaskSchema,
]);
export type IOccurrence = z.infer<typeof occurrenceSchema>;

export const homeVisitGroupSchema =
  unenrichedGroupOfHomeVisitActivityOccurrenceSchema
    .pick({
      id: true,
      category: true,
      start: true,
      end: true,
      timeOfDay: true,
      visitStatus: true,
    })
    .extend({
      ...assignees,
      // A group lists its activities by name and nothing more.
      occurrences: z.array(
        homeVisitSchema.pick({ activityId: true, id: true, title: true }),
      ),
    });
export const clinicVisitGroupSchema =
  unenrichedGroupOfClinicVisitActivityOccurrenceSchema
    .pick({
      id: true,
      category: true,
      start: true,
      end: true,
      timeOfDay: true,
      visitStatus: true,
    })
    .extend({
      ...assignees,
      occurrences: z.array(
        clinicVisitSchema.pick({ activityId: true, id: true, title: true }),
      ),
    });

const groupSchema = z.union([homeVisitGroupSchema, clinicVisitGroupSchema]);
export type IGroup = z.infer<typeof groupSchema>;

// `occurrences` is what separates a group from a single occurrence, the same way
// `isGroup` reads it, and it is required. Trying the group first therefore tells the
// two HomeVisit-category members apart without relying on a group failing to satisfy
// the occurrence schema.
export const occurrenceOrGroupSchema = z.union([groupSchema, occurrenceSchema]);
export type IOccurrenceOrGroup = z.infer<typeof occurrenceOrGroupSchema>;

const occurrencesAndGroupsSchema = z.array(occurrenceOrGroupSchema);

// The schedule asks for exactly the categories it can parse, read off the schemas
// above rather than written out a second time, so a category cannot be requested
// without a schema to parse it with. Both group categories are already
// contributed by their standalone occurrence schemas.
const requestedCategories = occurrenceSchema.options.flatMap((occurrence) => [
  ...occurrence.shape.category.values,
]);

export const scheduleKeys = {
  all: ["schedule"] as const,
};

const fetchMyActivityOccurrencesAndGroups = async () => {
  return await logisticsApi
    .get(
      `/occurrences${generateQueryString({
        category: requestedCategories,
        status: [
          activityStatusSchema.enum.active,
          activityStatusSchema.enum.closed,
        ],
        to: machineFormat(addDays(startOfToday(), 14), "yyyy-MM-dd"),
      })}`,
    )
    .json(occurrencesAndGroupsSchema);
};

export const getStatus = (activityOccurrenceOrGroup: IOccurrenceOrGroup) => {
  if (!isGroup(activityOccurrenceOrGroup)) {
    const activityOccurrence = activityOccurrenceOrGroup;
    return activityOccurrence.status;
  }
  const group = activityOccurrenceOrGroup;
  return group.visitStatus;
};

const fetchMySchedule = async () => {
  const activityOccurrencesAndGroups =
    await fetchMyActivityOccurrencesAndGroups();

  // SORTING
  const sortedActivityOccurrencesAndGroups = [
    ...activityOccurrencesAndGroups,
  ].sort((a, b) => {
    // Finished activityOccurrencesAndGroups should be displayed first, if they are on the same day
    if (
      getStatus(a) === "finished" &&
      getStatus(b) !== "finished" &&
      isSameDay(a.start, b.start)
    ) {
      return -1;
    }
    if (
      getStatus(b) === "finished" &&
      getStatus(a) !== "finished" &&
      isSameDay(a.start, b.start)
    ) {
      return 1;
    }
    // Sort any time of day activities last
    if (
      a.timeOfDay === timeOfDaySchema.enum.Any &&
      b.timeOfDay !== timeOfDaySchema.enum.Any &&
      isSameDay(a.start, b.start)
    ) {
      return 1;
    }
    if (
      b.timeOfDay === timeOfDaySchema.enum.Any &&
      a.timeOfDay !== timeOfDaySchema.enum.Any &&
      isSameDay(a.start, b.start)
    ) {
      return -1;
    }

    if (a.start < b.start) {
      return -1;
    }
    if (a.start > b.start) {
      return 1;
    }
    return 0;
  });

  // GROUPING
  const groupedSchedule = sortedActivityOccurrencesAndGroups.reduce(
    (groups, item) => {
      const lastGroup = groups[groups.length - 1];
      if (!lastGroup) {
        return groups;
      }
      if (lastGroup.length === 0) {
        lastGroup.push(item);
        return groups;
      }
      const lastItem = lastGroup[lastGroup.length - 1];
      if (!lastItem) {
        return groups;
      }
      if (lastItem.start.getDate() === item.start.getDate()) {
        lastGroup.push(item);
        return groups;
      }
      groups.push([item]);
      return groups;
    },
    [[]] as (typeof sortedActivityOccurrencesAndGroups)[],
  );

  return groupedSchedule;
};

const ONE_MINUTE = 60000;
export const useMySchedule = () => {
  const { data: patient } = usePatient();

  return useQuery({
    enabled: patient !== undefined,
    queryKey: scheduleKeys.all,
    queryFn: () => fetchMySchedule(),
    // Activity occurrences have time based status logic, so we need to refetch them often where status matters/is shown.
    refetchInterval: ONE_MINUTE,
  });
};
